import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Droplet, Wind, Activity, BookOpen, Waves, PenLine, Plus, Moon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICONS = { droplet: Droplet, wind: Wind, activity: Activity, book: BookOpen, wave: Waves, "pen-line": PenLine };
const MOOD_EMOJI = ["", "😞", "😕", "😐", "🙂", "😄"];

/** Day Ring — SVG radial dial of the waking day.
 *  Layers (outer → inner):
 *   • ring track (surface-container)
 *   • habits: petals placed at their time_of_day, glowing when done
 *   • movement arc: growing green arc toward daily goal (start of day → clockwise)
 *   • water dots: 8 small dots on inner ring, filling as water_count grows
 *   • sleep arc: soft tertiary arc showing last night's hours (0 → sleep_hours × 15° for reference)
 *  Center: day_light % + mood emoji + "how full" label.
 */
function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}
function arcPath(cx, cy, r, startDeg, endDeg) {
  const [sx, sy] = polar(cx, cy, r, startDeg);
  const [ex, ey] = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
}

function DayRing({ data, onLogHabit, size = 380 }) {
  const cx = size / 2, cy = size / 2;
  const rHabits = size / 2 - 20;
  const rMove   = size / 2 - 52;
  const rWater  = size / 2 - 84;

  const settings = data.settings;
  const wakingHours = settings.waking_end - settings.waking_start; // e.g. 17
  const now = new Date();
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const nowDeg = ((Math.max(settings.waking_start, Math.min(settings.waking_end, nowHour)) - settings.waking_start) / wakingHours) * 360;

  // Habit petals: place at time_of_day within waking window; otherwise distribute
  const habits = data.habits.filter((h) => h.required_today);
  const petals = habits.map((h, i) => {
    let hourVal;
    if (h.time_of_day) {
      const [hh, mm] = h.time_of_day.split(":").map(Number);
      hourVal = hh + (mm || 0) / 60;
    } else {
      hourVal = settings.waking_start + (i + 0.5) * (wakingHours / habits.length);
    }
    const clamped = Math.max(settings.waking_start, Math.min(settings.waking_end, hourVal));
    const deg = ((clamped - settings.waking_start) / wakingHours) * 360;
    return { habit: h, deg, done: h.log?.status === "done", rest: h.log?.status === "rest" };
  });

  const moveDeg = Math.min(360, (data.movement.minutes / data.movement.goal_min) * 360);
  const waterDots = Array.from({ length: data.checkin.water_goal }, (_, i) => i < data.checkin.water_count);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="max-w-full" role="img" aria-label={`Day ring at ${data.day_light}% complete`}>
      {/* Habit track */}
      <circle cx={cx} cy={cy} r={rHabits} fill="none" stroke="#ECE6F0" strokeWidth="6" />
      {/* Movement track */}
      <circle cx={cx} cy={cy} r={rMove} fill="none" stroke="#ECE6F0" strokeWidth="4" />
      {/* Water track */}
      <circle cx={cx} cy={cy} r={rWater} fill="none" stroke="#ECE6F0" strokeWidth="2" strokeDasharray="2 4" />

      {/* Movement arc — grows clockwise from top */}
      {moveDeg > 0 && (
        <path d={arcPath(cx, cy, rMove, 0.01, Math.min(moveDeg, 359.99))}
              fill="none" stroke="#0F5027" strokeWidth="6" strokeLinecap="round" opacity="0.85" />
      )}

      {/* Sleep arc — small tertiary indicator inside water ring at 6 o'clock */}
      {data.checkin.sleep_hours && (
        <path d={arcPath(cx, cy, rWater - 12, 180 - data.checkin.sleep_hours * 8, 180 + data.checkin.sleep_hours * 8)}
              fill="none" stroke="#7D5260" strokeWidth="4" strokeLinecap="round" opacity="0.7" />
      )}

      {/* Water dots */}
      {waterDots.map((filled, i) => {
        const deg = (i / data.checkin.water_goal) * 360;
        const [x, y] = polar(cx, cy, rWater, deg);
        return (
          <circle key={i} cx={x} cy={y} r="4"
                  fill={filled ? "#6750A4" : "#FFFBFE"}
                  stroke={filled ? "#6750A4" : "#CAC4D0"} strokeWidth="1.5" />
        );
      })}

      {/* Habit petals */}
      {petals.map(({ habit, deg, done, rest }) => {
        const [x, y] = polar(cx, cy, rHabits, deg);
        const IconEl = ICONS[habit.icon] || Activity;
        const fill = done ? habit.color : rest ? habit.color + "44" : "#FFFBFE";
        const stroke = done ? habit.color : habit.color + "99";
        return (
          <g key={habit.id} className="cursor-pointer" onClick={() => onLogHabit(habit)} data-testid={`ring-petal-${habit.id}`}>
            {/* glow bloom on done */}
            {done && <circle cx={x} cy={y} r="22" fill={habit.color} opacity="0.15" className="animate-pulse" />}
            <circle cx={x} cy={y} r="16" fill={fill} stroke={stroke} strokeWidth="2" />
            {/* icon foreignObject fallback: use small symbol via SVG */}
            <foreignObject x={x - 9} y={y - 9} width="18" height="18" style={{ pointerEvents: "none" }}>
              <div className="flex items-center justify-center w-full h-full">
                <IconEl className="w-4 h-4" style={{ color: done ? "#FFFBFE" : habit.color }} />
              </div>
            </foreignObject>
          </g>
        );
      })}

      {/* Now marker — small triangle pointing inward */}
      {(() => {
        const [x, y] = polar(cx, cy, rHabits + 10, nowDeg);
        const [xi, yi] = polar(cx, cy, rHabits + 2, nowDeg);
        return (
          <g>
            <line x1={x} y1={y} x2={xi} y2={yi} stroke="#1C1B1F" strokeWidth="2" strokeLinecap="round" />
            <circle cx={x} cy={y} r="3" fill="#1C1B1F" />
          </g>
        );
      })()}

      {/* Center */}
      <text x={cx} y={cy - 14} textAnchor="middle" className="fill-surface-on" style={{ fontSize: 42, fontWeight: 500, letterSpacing: "-0.02em" }}>
        {data.day_light}%
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" className="fill-surface-onVariant" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em" }}>
        day light
      </text>
      {data.checkin.mood && (
        <text x={cx} y={cy + 40} textAnchor="middle" style={{ fontSize: 24 }}>
          {MOOD_EMOJI[data.checkin.mood]}
        </text>
      )}
    </svg>
  );
}

export default function HealthToday() {
  const [data, setData] = useState(null);
  const [addMoveOpen, setAddMoveOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/health/today");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const logHabit = async (habit) => {
    const wasDone = habit.log?.status === "done";
    try {
      await api.post(`/habits/${habit.id}/log`, { status: "done" });
      if (!wasDone) toast.success(`${habit.name} — nice.`, { description: "One more thread of light." });
      await load();
    } catch { toast.error("Couldn't log — try again."); }
  };

  const bumpWater = async () => {
    if (!data) return;
    await api.post("/health/checkins", { water_count: (data.checkin.water_count || 0) + 1 });
    await load();
  };

  if (!data) return <div className="text-sm text-surface-onVariant">Loading your day…</div>;

  const message = data.day_light >= 80 ? "Beautiful. Your day is glowing."
    : data.day_light >= 50 ? "Nice rhythm today. Small threads matter."
    : data.day_light >= 20 ? "Gentle start. There's still light ahead."
    : "Take a soft breath. Anything counts.";

  return (
    <div data-testid="health-today" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Ring hero */}
        <div className="md:col-span-2 rounded-hero bg-surface-container p-6 md:p-8 flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-primary-container/50 blur-[80px] pointer-events-none" />
          <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-tertiary-container/50 blur-[90px] pointer-events-none" />
          <div className="relative">
            <DayRing data={data} onLogHabit={logHabit} />
          </div>
          <p className="relative text-center text-sm text-surface-onVariant mt-4 max-w-md" data-testid="health-message">{message}</p>
          {/* Accessibility text equivalent */}
          <p className="sr-only" data-testid="ring-text-summary">
            Today at {data.day_light}% day light. {data.counts.habits_done} of {data.counts.habits_required} habits complete.
            Moved {data.movement.minutes} of {data.movement.goal_min} minutes. Water {data.checkin.water_count} of {data.checkin.water_goal}.
            {data.checkin.sleep_hours ? ` Slept ${data.checkin.sleep_hours} hours.` : ""}
          </p>
        </div>

        {/* Side stats */}
        <div className="space-y-4">
          <StatCard label="Habits" value={`${data.counts.habits_done}/${data.counts.habits_required}`} icon={Wind} tone="primary" />
          <StatCard label="Movement" value={`${data.movement.minutes} min`} sub={`goal ${data.movement.goal_min} min`} icon={Activity} tone="success" />
          <StatCard label="Water" value={`${data.checkin.water_count}/${data.checkin.water_goal}`} icon={Droplet} tone="primary" />
          <StatCard label="Sleep" value={data.checkin.sleep_hours ? `${data.checkin.sleep_hours}h` : "—"} icon={Moon} tone="tertiary" />
        </div>
      </div>

      {/* Habit quick log strip */}
      <div className="rounded-hero bg-surface-container p-4">
        <div className="text-xs uppercase tracking-widest text-surface-onVariant mb-3">Today's habits</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="habit-strip">
          {data.habits.map((h) => {
            const done = h.log?.status === "done";
            const rest = h.log?.status === "rest";
            const IconEl = ICONS[h.icon] || Activity;
            return (
              <button
                key={h.id}
                data-testid={`habit-strip-${h.id}`}
                onClick={() => logHabit(h)}
                className={cn(
                  "rounded-2xl p-3 text-left transition-[background-color,transform] duration-200 ease-md active:scale-95",
                  done ? "text-primary-foreground" : "bg-surface hover:bg-primary/8 text-surface-on border border-outline-variant"
                )}
                style={done ? { backgroundColor: h.color } : {}}
              >
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: done ? "rgba(255,255,255,0.2)" : h.color + "22" }}>
                    <IconEl className="w-4 h-4" style={{ color: done ? "#FFFBFE" : h.color }} />
                  </div>
                  {done && <span className="text-[10px] font-medium tracking-wider uppercase">Done</span>}
                  {rest && <span className="text-[10px] font-medium tracking-wider uppercase text-surface-onVariant">Rest</span>}
                </div>
                <div className={cn("mt-2 text-sm font-medium", done ? "" : "text-surface-on")}>{h.name}</div>
                {h.time_of_day && <div className={cn("text-[10px] mt-0.5", done ? "text-primary-foreground/70" : "text-surface-onVariant")}>{h.time_of_day}</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick-log bar */}
      <div className="rounded-hero bg-surface-container p-4 flex flex-wrap gap-2" data-testid="quick-log-bar">
        <QuickBtn label="+ Water" onClick={bumpWater} testId="quick-water" />
        <QuickBtn label="+ Movement" onClick={() => setAddMoveOpen(true)} testId="quick-move" primary />
        <QuickBtn label="+ Sleep / Mood" onClick={() => setCheckinOpen(true)} testId="quick-checkin" />
      </div>

      <AddMovementDialog open={addMoveOpen} onOpenChange={setAddMoveOpen} onSaved={load} />
      <CheckinDialog open={checkinOpen} onOpenChange={setCheckinOpen} data={data.checkin} onSaved={load} />
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, tone }) {
  const toneColor = { primary: "text-primary", success: "text-success", tertiary: "text-tertiary" }[tone] || "text-surface-on";
  return (
    <div className="rounded-2xl bg-surface-container p-4 border border-outline-variant">
      <div className="flex items-center gap-2">
        <Icon className={cn("w-4 h-4", toneColor)} />
        <div className="text-xs uppercase tracking-widest text-surface-onVariant">{label}</div>
      </div>
      <div className="text-2xl font-medium text-surface-on tabular-nums mt-1.5">{value}</div>
      {sub && <div className="text-xs text-surface-onVariant">{sub}</div>}
    </div>
  );
}

function QuickBtn({ label, onClick, testId, primary }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full font-medium px-5 py-2.5 text-sm active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md",
        primary ? "bg-primary text-primary-foreground" : "bg-primary-container text-primary-onContainer"
      )}
    >{label}</button>
  );
}

function AddMovementDialog({ open, onOpenChange, onSaved }) {
  const [activity, setActivity] = useState("walk");
  const [duration, setDuration] = useState("");
  const [intensity, setIntensity] = useState("moderate");
  const [felt, setFelt] = useState("fine");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const dur = parseInt(duration || "0");
    if (dur <= 0) return toast.error("Enter minutes");
    setSaving(true);
    try {
      await api.post("/health/movement", { activity, duration_min: dur, intensity, felt });
      toast.success("Logged. Nicely done.");
      setDuration(""); onOpenChange(false); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="move-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Log movement</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-3">
          <div>
            <Label>Activity</Label>
            <Select value={activity} onValueChange={setActivity}>
              <SelectTrigger data-testid="move-activity" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {["walk", "run", "strength", "yoga", "swim", "cycle", "sport", "stretch", "other"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration" suffix="min">
              <input data-testid="move-duration" type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" required />
            </Field>
            <div>
              <Label>Intensity</Label>
              <Select value={intensity} onValueChange={setIntensity}>
                <SelectTrigger data-testid="move-intensity" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  {["light", "moderate", "vigorous"].map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>How did it feel?</Label>
            <div className="flex gap-2">
              {["energized", "fine", "drained"].map((f) => (
                <button key={f} type="button" onClick={() => setFelt(f)}
                        className={cn("flex-1 rounded-full px-3 py-2 text-sm transition-colors duration-200 ease-md",
                          felt === f ? "bg-primary text-primary-foreground" : "bg-surface-containerHigh text-surface-on hover:bg-primary/8")}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="move-save" type="submit" disabled={saving} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Log movement"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CheckinDialog({ open, onOpenChange, data, onSaved }) {
  const [sleep, setSleep] = useState(data?.sleep_hours || "");
  const [mood, setMood] = useState(data?.mood || 4);
  const [energy, setEnergy] = useState(data?.energy || 3);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setSleep(data?.sleep_hours || "");
      setMood(data?.mood || 4);
      setEnergy(data?.energy || 3);
    }
  }, [open, data]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { mood, energy };
      if (sleep) payload.sleep_hours = parseFloat(sleep);
      await api.post("/health/checkins", payload);
      toast.success("Thanks for checking in.");
      onOpenChange(false); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="checkin-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">How's today feeling?</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-3">
          <Field label="Hours slept last night" suffix="h">
            <input data-testid="checkin-sleep" type="number" step="0.5" min="0" max="24" value={sleep} onChange={(e) => setSleep(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" />
          </Field>
          <div>
            <Label>Mood</Label>
            <div className="flex gap-2">
              {MOOD_EMOJI.slice(1).map((emoji, i) => (
                <button key={i + 1} type="button" onClick={() => setMood(i + 1)}
                        className={cn("flex-1 rounded-2xl py-3 text-2xl transition-transform duration-200 ease-md",
                          mood === i + 1 ? "bg-primary-container scale-110" : "bg-surface-containerHigh hover:bg-primary/8")}
                        data-testid={`mood-${i + 1}`}>{emoji}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Energy: {energy}/5</Label>
            <input data-testid="checkin-energy" type="range" min="1" max="5" value={energy} onChange={(e) => setEnergy(parseInt(e.target.value))} className="w-full accent-primary" />
          </div>
          <button data-testid="checkin-save" type="submit" disabled={saving} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">{children}</div>;
}
function Field({ label, suffix, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5 transition-colors duration-200 ease-md">
        {children}
        {suffix && <span className="text-surface-onVariant text-sm ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
